// components/modals/servicehds/ServiceHDSModal.jsx
import React, { useRef, useState } from 'react';
import { Badge, Row, Col } from 'react-bootstrap';
import {
    FaServicestack,
    FaEdit,
    FaImage,
    FaTrash,
    FaUpload,
    FaTimes,
    FaCheck,
    FaClock,
    FaUser,
    FaCalendarAlt,
    FaInfoCircle,
    FaPlus,
    FaSave,
    FaTimes as FaClose,
    FaSpinner,
    FaCloudUploadAlt,
    FaFileImage
} from 'react-icons/fa';
import useServiceHDSModal from './hooks/useServiceHDSModal';
import { formatDateTime } from '../../../utils/dateHelper';
import '../../../assets/css/ServiceHDSPage.css';

const ServiceHDSModal = ({ service, isOpen, onClose, isEditMode, onSave }) => {
    const {
        editedService,
        subServices,
        isEditing,
        isSaving,
        isUploading,
        selectedFile,
        imagePreview,
        removeCurrentImage,
        fileInputRef,
        editingSubService,
        newSubService,
        isAddingSubService,
        handleInputChange,
        handleFileSelect,
        handleRemoveImage,
        handleCancelRemoveImage,
        handleSaveService,
        handleAddSubService,
        handleUpdateSubService,
        handleDeleteSubService,
        handleCancelEdit,
        setRemoveCurrentImage,
        setIsEditing,
        setEditingSubService,
        setNewSubService,
        setIsAddingSubService,
        getFrequencyLabel,
        getDisplayImage,
    } = useServiceHDSModal(service, isOpen, onClose, isEditMode, onSave);

    // États pour le drag & drop
    const [isDragOver, setIsDragOver] = useState(false);
    const dropZoneRef = useRef(null);

    if (!isOpen || !editedService) return null;

    // Composant d'affichage de champ
    const DisplayField = ({ label, value, icon, emptyText = "Non spécifié", children }) => (
        <div className="display-field mb-3">
            <div className="field-label">
                {icon} {label}
            </div>
            <div className="field-value bg-white rounded p-2">
                {children || value || emptyText}
            </div>
        </div>
    );

    // Gestion du drag & drop
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragOver) setIsDragOver(true);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            // Simuler un événement de changement de fichier
            const event = {
                target: {
                    files: [file]
                }
            };
            handleFileSelect(event);
        }
    };

    return (
        <>
            {/* Modal principal - Style Bottom Sheet */}
            <div
                className="service-modal-overlay show"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    zIndex: 1050,
                    overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="service-modal-container"
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh',
                        overflowY: 'auto',
                        animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe sticky */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 100,
                        backgroundColor: 'white',
                        borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px',
                        borderTopRightRadius: '24px',
                        padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    <FaServicestack className="me-2 text-primary" />
                                    {isEditing ? 'Modifier le service' : 'Détails du service'}
                                </h4>
                            </div>
                            <button
                                className="btn-close"
                                onClick={onClose}
                                disabled={isSaving || isUploading}
                            ></button>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-4">
                        {/* Image du service - Version améliorée avec Drop Zone */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-primary">
                                <FaImage className="me-2" />
                                Image du service
                            </h5>

                            {isEditing ? (
                                <div className="image-upload-section">
                                    {/* Drop Zone */}
                                    <div
                                        ref={dropZoneRef}
                                        className={`drop-zone ${isDragOver ? 'drop-zone-active' : ''} ${(selectedFile || getDisplayImage()) ? 'drop-zone-has-image' : ''}`}
                                        onDragEnter={handleDragEnter}
                                        onDragLeave={handleDragLeave}
                                        onDragOver={handleDragOver}
                                        onDrop={handleDrop}
                                        style={{
                                            border: `2px dashed ${isDragOver ? '#28a745' : '#cbd5e0'}`,
                                            borderRadius: '12px',
                                            padding: '20px',
                                            textAlign: 'center',
                                            backgroundColor: isDragOver ? 'rgba(40, 167, 69, 0.05)' : 'white',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            minHeight: '200px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/svg+xml"
                                            onChange={handleFileSelect}
                                            className="file-input"
                                            disabled={isSaving || isUploading}
                                            style={{ display: 'none' }}
                                        />

                                        {selectedFile || getDisplayImage() ? (
                                            // Aperçu avec overlay
                                            <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
                                                <img
                                                    src={selectedFile ? imagePreview : getDisplayImage()}
                                                    alt="Aperçu du service"
                                                    style={{
                                                        width: '100%',
                                                        maxHeight: '200px',
                                                        objectFit: 'contain',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '8px',
                                                    display: 'flex',
                                                    gap: '8px'
                                                }}>
                                                    {selectedFile && (
                                                        <Badge bg="success" className="rounded-pill px-3 py-2">
                                                            <FaCheck className="me-1" /> Nouvelle image
                                                        </Badge>
                                                    )}
                                                    {removeCurrentImage && (
                                                        <Badge bg="danger" className="rounded-pill px-3 py-2">
                                                            <FaTimes className="me-1" /> Supprimée
                                                        </Badge>
                                                    )}
                                                </div>
                                                {(selectedFile || removeCurrentImage) && (
                                                    <button
                                                        className="btn btn-danger btn-sm position-absolute"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveImage();
                                                        }}
                                                        type="button"
                                                        disabled={isSaving || isUploading}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '8px',
                                                            left: '8px',
                                                            width: '32px',
                                                            height: '32px',
                                                            padding: 0,
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <FaTimes size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            // État vide
                                            <>
                                                <FaCloudUploadAlt
                                                    size={48}
                                                    style={{
                                                        color: isDragOver ? '#28a745' : '#a0aec0',
                                                        marginBottom: '12px',
                                                        transition: 'color 0.3s ease'
                                                    }}
                                                />
                                                <p style={{
                                                    fontWeight: 500,
                                                    color: isDragOver ? '#28a745' : '#4a5568',
                                                    marginBottom: '4px'
                                                }}>
                                                    {isDragOver ? 'Déposez votre image ici' : 'Glissez-déposez votre image'}
                                                </p>
                                                <p style={{
                                                    fontSize: '14px',
                                                    color: '#a0aec0',
                                                    marginBottom: '12px'
                                                }}>
                                                    ou cliquez pour parcourir
                                                </p>
                                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                    <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                                                        <FaFileImage className="me-1" /> JPEG
                                                    </Badge>
                                                    <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                                                        <FaFileImage className="me-1" /> PNG
                                                    </Badge>
                                                    <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                                                        <FaFileImage className="me-1" /> WEBP
                                                    </Badge>
                                                    <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                                                        <FaFileImage className="me-1" /> GIF
                                                    </Badge>
                                                </div>
                                                <small className="text-muted mt-2">
                                                    Max: 10 Mo
                                                </small>
                                            </>
                                        )}
                                    </div>

                                    {/* Actions supplémentaires */}
                                    {(selectedFile || getDisplayImage()) && (
                                        <div className="mt-3 d-flex flex-wrap gap-2 justify-content-center">
                                            {!selectedFile && !removeCurrentImage && editedService.mediaUrl && (
                                                <button
                                                    className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                                    onClick={() => setRemoveCurrentImage(true)}
                                                    type="button"
                                                    disabled={isSaving || isUploading}
                                                >
                                                    <FaTrash className="me-1" /> Supprimer l'image actuelle
                                                </button>
                                            )}
                                            {removeCurrentImage && (
                                                <button
                                                    className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                                                    onClick={handleCancelRemoveImage}
                                                    type="button"
                                                    disabled={isSaving || isUploading}
                                                >
                                                    <FaTimes className="me-1" /> Annuler la suppression
                                                </button>
                                            )}
                                            {selectedFile && (
                                                <div className="text-muted small d-flex align-items-center">
                                                    <FaInfoCircle className="me-1" />
                                                    {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} Mo)
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // Mode visualisation
                                <div className="image-display text-center">
                                    {editedService.mediaUrlPresigned || editedService.mediaUrl ? (
                                        <img
                                            src={editedService.mediaUrlPresigned || editedService.mediaUrl}
                                            alt={editedService.name}
                                            className="rounded-3"
                                            style={{
                                                maxWidth: '300px',
                                                maxHeight: '300px',
                                                objectFit: 'cover',
                                                border: '2px solid #e2e8f0'
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                const placeholder = e.target.parentElement.querySelector('.no-image-large');
                                                if (placeholder) {
                                                    placeholder.style.display = 'block';
                                                }
                                            }}
                                        />
                                    ) : null}
                                    {!editedService.mediaUrlPresigned && !editedService.mediaUrl && (
                                        <div className="no-image-large text-center py-4">
                                            <FaImage size={48} className="text-muted mb-2" />
                                            <p className="text-muted">Aucune image</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Informations du service */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-info">
                                <FaServicestack className="me-2" />
                                Informations du service
                            </h5>
                            <Row className="g-3">
                                <Col md={12}>
                                    <DisplayField
                                        label="Nom du service"
                                        value={editedService.name}
                                        icon={<FaServicestack className="text-primary" />}
                                    >
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                name="name"
                                                value={editedService.name || ''}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                disabled={isSaving || isUploading}
                                                required
                                            />
                                        ) : (
                                            editedService.name
                                        )}
                                    </DisplayField>
                                </Col>
                                <Col md={12}>
                                    <DisplayField
                                        label="Description"
                                        value={editedService.description || 'Non spécifié'}
                                        icon={<FaInfoCircle className="text-info" />}
                                    >
                                        {isEditing ? (
                                            <textarea
                                                name="description"
                                                value={editedService.description || ''}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                rows="3"
                                                disabled={isSaving || isUploading}
                                            />
                                        ) : (
                                            editedService.description || 'Non spécifié'
                                        )}
                                    </DisplayField>
                                </Col>
                            </Row>
                        </div>

                        {/* Section Sous-services - inchangée */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="fw-bold mb-0 text-success">
                                    <FaServicestack className="me-2" />
                                    Sous-services ({subServices.length})
                                </h5>
                                {!isAddingSubService && !editingSubService && isEditing && (
                                    <button
                                        className="btn btn-success btn-sm rounded-pill px-3 d-flex align-items-center gap-2 w-auto"
                                        style={{ maxWidth: '200px' }}
                                        onClick={() => setIsAddingSubService(true)}
                                        type="button"
                                        disabled={isSaving || isUploading}
                                    >
                                        <FaPlus size={12} /> Ajouter
                                    </button>
                                )}
                            </div>

                            {/* Formulaire d'ajout de sous-service */}
                            {isAddingSubService && (
                                <div className="bg-white rounded-3 p-3 mb-3">
                                    <h6 className="fw-bold mb-3 text-success">Nouveau Sous-service</h6>
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <input
                                                type="text"
                                                value={newSubService.name}
                                                onChange={(e) => setNewSubService({...newSubService, name: e.target.value})}
                                                className="form-control"
                                                placeholder="Nom du sous-service"
                                                disabled={isSaving}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={newSubService.amount}
                                                onChange={(e) => setNewSubService({...newSubService, amount: e.target.value})}
                                                className="form-control"
                                                placeholder="Montant (FCFA)"
                                                disabled={isSaving}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <select
                                                value={newSubService.frequency}
                                                onChange={(e) => setNewSubService({...newSubService, frequency: e.target.value})}
                                                className="form-control"
                                                disabled={isSaving}
                                            >
                                                <option value="LUNDI_A_VENDREDI">De lundi à vendredi</option>
                                                <option value="CHAQUE_JOUR">Chaque jour</option>
                                                <option value="TROIS_JOURS_PAR_SEMAINE">3 jours par semaine</option>
                                            </select>
                                        </Col>
                                        <Col md={2}>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-success btn-sm rounded-pill px-3"
                                                    onClick={handleAddSubService}
                                                    disabled={isSaving}
                                                >
                                                    <FaCheck /> Ajouter
                                                </button>
                                                <button
                                                    className="btn btn-secondary btn-sm rounded-pill px-3"
                                                    onClick={() => setIsAddingSubService(false)}
                                                    disabled={isSaving}
                                                >
                                                    <FaTimes /> Annuler
                                                </button>
                                            </div>
                                        </Col>
                                    </Row>
                                    <small className="text-muted">
                                        Fréquence sélectionnée : {getFrequencyLabel(newSubService.frequency)}
                                    </small>
                                </div>
                            )}

                            {/* Formulaire de modification de sous-service */}
                            {editingSubService && (
                                <div className="bg-white rounded-3 p-3 mb-3">
                                    <h6 className="fw-bold mb-3 text-warning">Modifier le Sous-service</h6>
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <input
                                                type="text"
                                                value={editingSubService.name}
                                                onChange={(e) => setEditingSubService({...editingSubService, name: e.target.value})}
                                                className="form-control"
                                                disabled={isSaving}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={editingSubService.amount}
                                                onChange={(e) => setEditingSubService({...editingSubService, amount: e.target.value})}
                                                className="form-control"
                                                disabled={isSaving}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <select
                                                value={editingSubService.frequency}
                                                onChange={(e) => setEditingSubService({...editingSubService, frequency: e.target.value})}
                                                className="form-control"
                                                disabled={isSaving}
                                            >
                                                <option value="LUNDI_A_VENDREDI">De lundi à vendredi</option>
                                                <option value="CHAQUE_JOUR">Chaque jour</option>
                                                <option value="TROIS_JOURS_PAR_SEMAINE">3 jours par semaine</option>
                                            </select>
                                        </Col>
                                        <Col md={2}>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-warning btn-sm rounded-pill px-3"
                                                    onClick={handleUpdateSubService}
                                                    disabled={isSaving}
                                                >
                                                    <FaSave /> Modifier
                                                </button>
                                                <button
                                                    className="btn btn-secondary btn-sm rounded-pill px-3"
                                                    onClick={() => setEditingSubService(null)}
                                                    disabled={isSaving}
                                                >
                                                    <FaTimes /> Annuler
                                                </button>
                                            </div>
                                        </Col>
                                    </Row>
                                    <small className="text-muted">
                                        Fréquence sélectionnée : {getFrequencyLabel(editingSubService.frequency)}
                                    </small>
                                </div>
                            )}

                            {/* Liste des sous-services */}
                            {subServices.length > 0 ? (
                                <div className="sub-services-list">
                                    {subServices.map((subService) => (
                                        <div
                                            key={subService.id}
                                            className="d-flex justify-content-between align-items-center bg-white rounded-3 p-3 mb-2"
                                        >
                                            <div>
                                                <h6 className="fw-bold mb-1">{subService.name}</h6>
                                                <div className="d-flex gap-2">
                                                    <Badge bg="primary" className="rounded-pill px-3 py-2">
                                                        {subService.amount} FCFA
                                                    </Badge>
                                                    <Badge bg="info" className="rounded-pill px-3 py-2">
                                                        {getFrequencyLabel(subService.frequency)}
                                                    </Badge>
                                                </div>
                                            </div>
                                            {isEditing && (
                                                <div className="d-flex gap-2">
                                                    <button
                                                        className="btn btn-outline-primary btn-sm rounded-circle"
                                                        onClick={() => setEditingSubService(subService)}
                                                        title="Modifier"
                                                        disabled={isSaving}
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            padding: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <FaEdit size={14} />
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-danger btn-sm rounded-circle"
                                                        onClick={() => handleDeleteSubService(subService.id, subService.name)}
                                                        title="Supprimer"
                                                        disabled={isSaving}
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            padding: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <FaTrash size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 bg-white rounded-3">
                                    <p className="text-muted mb-0">Aucun sous-service configuré</p>
                                </div>
                            )}
                        </div>

                        {/* Métadonnées - inchangées */}
                        {!isEditing && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-secondary">
                                    <FaInfoCircle className="me-2" />
                                    Informations Techniques
                                </h5>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <DisplayField
                                            label="Créé par"
                                            value={editedService.createdBy || 'Non spécifié'}
                                            icon={<FaUser className="text-primary" />}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <DisplayField
                                            label="Date de création"
                                            value={formatDateTime(editedService.createdAt)}
                                            icon={<FaCalendarAlt className="text-primary" />}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <DisplayField
                                            label="Dernière modification par"
                                            value={editedService.lastModifiedBy || 'Non spécifié'}
                                            icon={<FaUser className="text-warning" />}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <DisplayField
                                            label="Date de modification"
                                            value={formatDateTime(editedService.modifiedAt)}
                                            icon={<FaCalendarAlt className="text-warning" />}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>

                    {/* Footer fixe avec actions - inchangé */}
                    <div className="modal-footer-fixed" style={{
                        position: 'sticky',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px',
                        borderBottomLeftRadius: '24px',
                        borderBottomRightRadius: '24px'
                    }}>
                        <div className="d-flex justify-content-end gap-3">
                            {isEditing ? (
                                <>
                                    <button
                                        className="btn btn-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                        onClick={handleCancelEdit}
                                        disabled={isSaving || isUploading}
                                        type="button"
                                    >
                                        <FaTimes /> Annuler
                                    </button>
                                    <button
                                        className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                                        onClick={handleSaveService}
                                        disabled={isSaving || isUploading || !editedService.name}
                                        type="button"
                                    >
                                        {isSaving && isUploading && <FaSpinner className="fa-spin" />}
                                        {isSaving && !isUploading && 'Sauvegarde...'}
                                        {isUploading && !isSaving && 'Upload...'}
                                        {!isSaving && !isUploading && <><FaSave /> Enregistrer</>}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        className="btn btn-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                        onClick={onClose}
                                        type="button"
                                    >
                                        <FaClose /> Fermer
                                    </button>
                                    <button
                                        className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                                        onClick={() => setIsEditing(true)}
                                        type="button"
                                    >
                                        <FaEdit /> Modifier le Service
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Styles CSS intégrés */}
            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes fa-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .service-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .fa-spin {
                    animation: fa-spin 1s linear infinite;
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                }

                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }

                .display-field .form-control {
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 8px 12px;
                }

                .display-field .form-control:focus {
                    border-color: #28a745;
                    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
                }

                /* Styles du Drop Zone */
                .drop-zone {
                    transition: all 0.3s ease;
                    border: 2px dashed #cbd5e0;
                    border-radius: 12px;
                    padding: 20px;
                    text-align: center;
                    background-color: white;
                    cursor: pointer;
                    min-height: 200px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                }

                .drop-zone:hover {
                    border-color: #28a745;
                    background-color: rgba(40, 167, 69, 0.03);
                }

                .drop-zone-active {
                    border-color: #28a745 !important;
                    background-color: rgba(40, 167, 69, 0.08) !important;
                    transform: scale(1.02);
                }

                .drop-zone-has-image {
                    padding: 12px;
                    min-height: auto;
                }

                .drop-zone img {
                    max-width: 100%;
                    max-height: 200px;
                    object-fit: contain;
                    border-radius: 8px;
                }

                .drop-zone .badge {
                    font-size: 0.7rem;
                    padding: 6px 12px;
                }
            `}</style>
        </>
    );
};

export default ServiceHDSModal;